/**
 * Contacts Service
 * 
 * API service for managing contacts. Contacts are people who receive
 * greeting cards. Each contact can have a birthday, custom dates, and
 * subscriptions to event types.
 */

import axios from 'axios';
import apiClient from './apiClient';
import { trackContactCreated } from './marketingTracking';
import type {
  Contact,
  CreateContactRequest,
  UpdateContactRequest,
  ContactCustomDate,
  ContactEventTypeService,
} from '../types';
import { ScheduleMode, ServiceType } from '../types';

/**
 * Fetches all contacts for the current authenticated user.
 * Includes custom dates and event type subscriptions.
 * @returns Promise resolving to array of contacts
 */
export const getContacts = async (): Promise<Contact[]> => {
  const response = await apiClient.get<Contact[]>('/contacts');
  return response.data;
};

/**
 * Fetches a single contact by ID.
 * Includes custom dates and event type subscriptions.
 * @param id - The contact ID
 * @returns Promise resolving to the contact
 */
export const getContact = async (id: number): Promise<Contact> => {
  const response = await apiClient.get<Contact>(`/contacts/${id}`);
  return response.data;
};

/**
 * Creates a new contact for the current user.
 * @param contact - The contact data to create
 * @returns Promise resolving to the created contact
 */
export const createContact = async (contact: CreateContactRequest): Promise<Contact> => {
  const response = await apiClient.post<Contact>('/contacts', contact);
  trackContactCreated();
  return response.data;
};

/**
 * Updates an existing contact.
 * @param id - The contact ID
 * @param contact - The updated contact data
 * @returns Promise resolving when update is complete
 */
export const updateContact = async (id: number, contact: UpdateContactRequest): Promise<void> => {
  await apiClient.put(`/contacts/${id}`, contact);
};

/** One row from DELETE /contacts/{id} 409 preview (blockers or warnings). */
export interface DeletionImpactItem {
  entityType: string;
  entityId: number;
  reason: string;
  recommendedAction?: string | null;
}

/** Body of HTTP 409 from contact delete when dependents block or warn. */
export interface ContactDeleteImpact {
  blockers: DeletionImpactItem[];
  warnings: DeletionImpactItem[];
}

/** Result of a delete attempt — caller branches on status for modal UX. */
export type DeleteContactResult =
  | { status: 'deleted' }
  | { status: 'blocked'; impact: ContactDeleteImpact }
  | { status: 'warning'; impact: ContactDeleteImpact };

/** Result of event/service removal — same shape as contact delete. */
export type RemovalAttemptResult = DeleteContactResult;

const parseRemoval409 = (data: ContactDeleteImpact): RemovalAttemptResult => {
  const blockers = data?.blockers ?? [];
  if (blockers.length > 0) {
    return { status: 'blocked', impact: data };
  }
  return { status: 'warning', impact: data };
};

/**
 * Soft-deletes a contact. First call (no acknowledge) surfaces 409 with impact;
 * call with options.acknowledge after the user confirms warnings.
 */
export const deleteContact = async (
  id: number,
  options?: { acknowledge?: boolean },
): Promise<DeleteContactResult> => {
  try {
    await apiClient.delete(`/contacts/${id}`, {
      params: options?.acknowledge ? { acknowledge: true } : {},
    });
    return { status: 'deleted' };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const data = error.response.data as ContactDeleteImpact;
      const blockers = data?.blockers ?? [];
      if (blockers.length > 0) {
        return { status: 'blocked', impact: data };
      }
      return { status: 'warning', impact: data };
    }
    throw error;
  }
};

/**
 * Adds a custom date to a contact.
 * @param contactId - The contact ID
 * @param customDate - The custom date to add
 * @returns Promise resolving to the created custom date
 */
export const addCustomDate = async (
  contactId: number,
  customDate: Omit<ContactCustomDate, 'id'>
): Promise<ContactCustomDate> => {
  const response = await apiClient.post<ContactCustomDate>(
    `/contacts/${contactId}/custom-dates`,
    customDate
  );
  return response.data;
};

/**
 * Subscribes a contact to an event type.
 * @param contactId - The contact ID
 * @param eventTypeId - The event type ID
 * @returns Promise resolving when subscription is complete
 */
export const subscribeToEventType = async (
  contactId: number,
  eventTypeId: number
): Promise<void> => {
  await apiClient.post(`/contacts/${contactId}/event-types/${eventTypeId}`);
};

/**
 * Unsubscribes a contact from an event type.
 * @param contactId - The contact ID
 * @param eventTypeId - The event type ID
 * @returns Promise resolving when unsubscription is complete
 */
export const unsubscribeFromEventType = async (
  contactId: number,
  eventTypeId: number,
  options?: { acknowledge?: boolean },
): Promise<RemovalAttemptResult> => {
  try {
    await apiClient.delete(`/contacts/${contactId}/event-types/${eventTypeId}`, {
      params: options?.acknowledge ? { acknowledge: true } : {},
    });
    return { status: 'deleted' };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return parseRemoval409(error.response.data as ContactDeleteImpact);
    }
    throw error;
  }
};

/**
 * Adds a service enrollment to a contact-event subscription.
 * Creates the service row with default ScheduleMode = Notify.
 * @param contactId - The contact ID
 * @param eventTypeId - The event type ID
 * @param serviceType - The service type to enroll
 * @returns Promise resolving to the created service enrollment
 */
export const addServiceForEventType = async (
  contactId: number,
  eventTypeId: number,
  serviceType: ServiceType
): Promise<ContactEventTypeService> => {
  const response = await apiClient.post<ContactEventTypeService>(
    `/contacts/${contactId}/event-types/${eventTypeId}/services`,
    { serviceType }
  );
  return response.data;
};

/**
 * Removes a service enrollment from a contact-event subscription.
 * Returns 409 if pending scheduled items exist for this service.
 * @param contactId - The contact ID
 * @param eventTypeId - The event type ID
 * @param serviceType - The service type to remove
 * @returns Promise resolving when the removal is complete
 */
export const removeServiceFromEventType = async (
  contactId: number,
  eventTypeId: number,
  serviceType: ServiceType,
  options?: { acknowledge?: boolean },
): Promise<RemovalAttemptResult> => {
  try {
    await apiClient.delete(
      `/contacts/${contactId}/event-types/${eventTypeId}/services/${serviceType}`,
      { params: options?.acknowledge ? { acknowledge: true } : {} },
    );
    return { status: 'deleted' };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return parseRemoval409(error.response.data as ContactDeleteImpact);
    }
    throw error;
  }
};

/**
 * Updates the schedule mode for a specific service enrollment.
 * Controls how the system handles upcoming events: Off, Remind, or Auto Send.
 * @param contactId - The contact ID
 * @param eventTypeId - The event type ID
 * @param serviceType - The service type
 * @param scheduleMode - The desired schedule mode
 * @returns Promise resolving when the update is complete
 */
export const setServiceScheduleMode = async (
  contactId: number,
  eventTypeId: number,
  serviceType: ServiceType,
  scheduleMode: ScheduleMode
): Promise<void> => {
  await apiClient.put(
    `/contacts/${contactId}/event-types/${eventTypeId}/services/${serviceType}/schedule-mode`,
    { scheduleMode }
  );
};

/** Request body for PUT .../services/MailedCard/mailed-card-settings (PBI 1005). */
export interface UpdateMailedCardSettingsRequest {
  mailedCardDefaultMessage: string | null;
  mailedCardShippingMethod: string;
}

/**
 * Updates mailed-card Auto Send preferences on a service enrollment.
 * @param contactId - The contact ID
 * @param eventTypeId - The event type ID
 * @param settings - Optional fallback message and shipping method
 * @returns Promise resolving to the updated service enrollment
 */
export const setMailedCardSettings = async (
  contactId: number,
  eventTypeId: number,
  settings: UpdateMailedCardSettingsRequest,
): Promise<ContactEventTypeService> => {
  const response = await apiClient.put<ContactEventTypeService>(
    `/contacts/${contactId}/event-types/${eventTypeId}/services/MailedCard/mailed-card-settings`,
    settings,
  );
  return response.data;
};

